"use client"

/**
 * Document imports, modelled as a record per document rather than one global
 * job.
 *
 * Reading a paper takes two to five minutes in production. That is long enough
 * that the run has to outlive whatever surface started it: an author who walks
 * the pool, opens a preview, or reloads the tab has not cancelled anything, and
 * coming back to a spinner reset to zero is the worst possible reading of what
 * happened. So the run lives here, in the module, not in a component:
 *
 * - **The clock is a deadline, not a countdown.** State is `startedAt` and
 *   `finishAt`, and the only thing derived from the wall clock is how long the
 *   read has been going. Unmounting, remounting, or reloading lands on the
 *   real elapsed time, because nothing about it was living in a React tree.
 *   There is no percentage and no estimate: a background read reports that it
 *   is finished and nothing else, so any bar drawn over it would be fiction.
 * - **It is persisted.** A reload during a four-minute read is a normal thing
 *   to do and must not lose the run.
 * - **The outcome is per document, and it sticks.** One author sitting imports
 *   two or three documents, and "which of these is in, which failed" is a fact
 *   about each document, not about the last thing that ran. A record survives
 *   its run so the list can keep saying it.
 * - **Several can run at once.** Each document is its own record with its own
 *   deadline. Authors often queue a second paper while the first is still
 *   reading; blocking that made them wait on a spinner for no product reason.
 *   The page banner speaks for the newest running job (or the latest settled
 *   outcome); every other job stays reachable from Recent.
 *
 * The extraction is mocked, so `finishAt` is a plausible duration rather than
 * real work. Everything the UI reads is the shape a real queue would give it:
 * start, poll, cancel, review, take, discard.
 *
 * The record also carries the **questions themselves**, along with the
 * reviewer's decision on each one. That is what makes the review route
 * resumable: closing the tab halfway through triaging sixteen questions is a
 * normal thing to do, and coming back to a reset batch would be as bad as
 * coming back to a reset progress bar. Committing is partial by construction
 * (`commitQuestionImport`), so a batch with two broken questions in it still
 * lands the other fourteen.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import type {
  ExtractedQuestion,
  ImportDuplicateResolution,
  ImportItemDecision,
  ImportSourceKind,
  ImportedQuestionDraft,
} from "@/lib/question-import-model"
import { isBlocked, willImport } from "@/lib/question-import-model"
import type { ImportApproach } from "@/hooks/use-import-approach"
import {
  mockExtractedQuestions,
  usesSeededMidtermRead,
} from "@/lib/mock/question-import-extraction"
import {
  clearQuestionImportNotification,
  notifyQuestionImportChange,
} from "@/lib/question-import-notifications"

/**
 * A document with no record has not been read, which is why there is no member
 * for it here: absence is the state, so nothing has to be written when the
 * sheet first lists a reference it has never touched.
 *
 * The ladder the UI draws from this is Processing, Pending review, Imported,
 * with Failed off to the side. `running` and `done` are the store's words for
 * the first two; the labels live at the callsites.
 */
export type QuestionImportStatus =
  | "running"
  /** Read, and waiting on the author to look at what it turned up. */
  | "done"
  /** Reviewed and taken into the pool. Terminal. */
  | "imported"
  | "failed"

/** What landed, kept so the banner after a commit can say it without guessing. */
export interface QuestionImportCommit {
  at: number
  imported: number
  rejected: number
  /** Left pending or still blocked when the author committed the rest. */
  skipped: number
  folderName: string | null
  asDraft: boolean
}

/** Where the author started the read. Older records omit it and stay on assessment. */
export type QuestionImportHome = "bank" | "assessment"

export interface QuestionImportRecord {
  /** Reference id, or `upload:<name>` for a file the author dropped in. */
  id: string
  /** Carried on the record so a reload can still name a running upload. */
  name: string
  status: QuestionImportStatus
  startedAt: number
  finishAt: number
  /** Questions the read turned up. Only meaningful once past `running`. */
  found: number
  /** Set to fail the run, in the author's words rather than the parser's. */
  failure?: string
  /** Document read or parsed table. Decides whether confidence means anything. */
  kind: ImportSourceKind
  /**
   * The questions themselves, with the reviewer's decisions on them.
   *
   * Written at `start`, like `found` is, and for the same reason: the
   * extraction is mocked, so there is nothing to wait for except the clock.
   * A real pipeline would fill this in at settle time and nothing above here
   * would need to change, because no surface reads `items` while `running`.
   */
  items: ExtractedQuestion[]
  /** Library folder the batch lands in. `null` means the bank root. */
  destinationFolderId: string | null
  /**
   * Course a question-bank import is for. Assessment imports already belong
   * to the assessment's course, so this stays unset there.
   */
  courseId?: string | null
  /**
   * Which surface started the read. Banners stay on that surface: a question
   * bank import must not paint on assessment details.
   */
  home?: QuestionImportHome
  /** Land them editable rather than as committed revisions. */
  asDraft: boolean
  /**
   * How this job finishes. Stamped at start so flipping the profile preference
   * mid-read does not change the outcome of a run already underway. Older
   * persisted records omit it and read as `"review"`.
   */
  approach?: ImportApproach
  commit?: QuestionImportCommit
}

export type QuestionImportDocs = Record<string, QuestionImportRecord>

/**
 * Long enough that the background model is the point and not decoration, short
 * enough to sit through once while judging the flow. Production reads run two
 * to five minutes; the copy says so, and nothing in the UI is keyed to this
 * number.
 */
export const QUESTION_IMPORT_MOCK_RUN_MS = 28_000

const STORAGE_KEY = "question-import-docs"
/**
 * Bumped to 9 when the hand hygiene blank dropped its imported rationale.
 * Older jobs are dropped.
 */
const VERSION = 9

const EMPTY: QuestionImportDocs = {}

const listeners = new Set<() => void>()
let snapshot: QuestionImportDocs = EMPTY
let hydrated = false
/**
 * One wake-up per run, at its deadline. The store only has news when a read
 * finishes; the per-second stage and bar are derived from the clock by whoever
 * draws them (`useQuestionImportProgress`), because a snapshot that changed
 * identity every second would re-render every reader of the docs for a number
 * most of them do not show.
 */
let ticker: number | null = null
let tickerDeadline: number | null = null

function emit() {
  for (const listener of listeners) listener()
}

function announceImportTransitions(
  previous: QuestionImportDocs,
  next: QuestionImportDocs
) {
  if (previous === next) return
  for (const id of Object.keys(previous)) {
    if (!(id in next)) clearQuestionImportNotification(id)
  }
  for (const record of Object.values(next)) {
    const before = previous[record.id]
    if (!before) {
      notifyQuestionImportChange(record)
      continue
    }
    if (
      before.status === record.status &&
      before.found === record.found &&
      before.commit?.at === record.commit?.at
    ) {
      continue
    }
    notifyQuestionImportChange(record)
  }
}

function persist(docs: QuestionImportDocs) {
  const empty = Object.keys(docs).length === 0
  setStorageItem(STORAGE_KEY, empty ? "" : JSON.stringify({ v: VERSION, d: docs }))
}

function isRecord(value: unknown): value is QuestionImportRecord {
  if (typeof value !== "object" || value === null) return false
  const record = value as Partial<QuestionImportRecord>
  return (
    typeof record.id === "string" &&
    typeof record.name === "string" &&
    typeof record.startedAt === "number" &&
    typeof record.finishAt === "number" &&
    typeof record.found === "number" &&
    Array.isArray(record.items) &&
    (record.status === "running" ||
      record.status === "done" ||
      record.status === "imported" ||
      record.status === "failed")
  )
}

/**
 * Settles any run whose deadline has passed. Called on every read, so a tab
 * that was asleep, backgrounded, or closed through the finish still sees the
 * result the moment it looks — there is no missed-timer state to recover from.
 */
function extractionSignature(items: ExtractedQuestion[]): string {
  return items
    .map((item) => {
      const options = item.draft.options?.join("\u001f") ?? ""
      const answers = item.draft.answerIndexes?.join(",") ?? ""
      const pairs =
        item.draft.pairs
          ?.map((pair) => `${pair.left}=${pair.right}`)
          .join("\u001f") ?? ""
      return `${item.ordinal}\u001e${item.draft.type}\u001e${item.draft.stem}\u001e${options}\u001e${answers}\u001e${pairs}\u001e${item.confidence}`
    })
    .join("\u001d")
}

function plainImportStem(stem: string): string {
  return stem
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

/** A reviewer already rewrote, resolved, or took something in this batch. */
function importReviewHasProgress(record: QuestionImportRecord): boolean {
  return record.items.some(
    (item) =>
      item.reviewResolved === true ||
      item.importedAt !== null ||
      item.decision === "accepted" ||
      item.decision === "rejected"
  )
}

/**
 * True when a stem no longer matches the seed at the same ordinal.
 * Option-only drift (the old short-distractor parse) stays false so that
 * parse can still be replaced. A rewritten stem is a review, and must stay.
 */
function importStemsDiverged(
  current: ExtractedQuestion[],
  seeded: ExtractedQuestion[]
): boolean {
  if (current.length !== seeded.length) return false
  return current.some((item, index) => {
    const seed = seeded[index]
    if (!seed) return false
    return plainImportStem(item.draft.stem) !== plainImportStem(seed.draft.stem)
  })
}

/**
 * Chrome can still be holding the earlier parse of the handed-out midterm.
 * That read is what painted the short distractors. Replace it with the seeded
 * questions the moment the store is read.
 *
 * A batch that already matches the seed is left alone. So is a review in
 * progress: a rewritten stem, a resolved caution, or a question already taken.
 * Replacing those on the next read put the old stem and Worth a look back
 * the moment Leo applied a variation.
 */
function extractedBodyKey(item: ExtractedQuestion): string {
  const pairs =
    item.draft.pairs?.map((pair) => `${pair.left}=${pair.right}`).join("\u001f") ??
    ""
  return [
    plainImportStem(item.draft.stem),
    item.draft.options.join("\u001f"),
    item.draft.answerIndexes.join(","),
    pairs,
  ].join("\u001e")
}

/**
 * Worth a look is the extractor's caution. It stays until the stem or the
 * choices actually change. A flag left on the original wording (the editor
 * hydrating the stem into HTML) was hiding the tag while Leo still flagged
 * the question.
 */
function withoutStaleReviewFlags(
  items: ExtractedQuestion[],
  seeded: ExtractedQuestion[]
): ExtractedQuestion[] | null {
  let changed = false
  const next = items.map((item, index) => {
    if (item.reviewResolved !== true) return item
    const seed = seeded.find((entry) => entry.ordinal === item.ordinal) ?? seeded[index]
    if (!seed || extractedBodyKey(item) !== extractedBodyKey(seed)) return item
    changed = true
    const { reviewResolved: _stale, ...rest } = item
    return rest
  })
  return changed ? next : null
}

function refreshSeededMidtermReads(docs: QuestionImportDocs): QuestionImportDocs {
  let next: QuestionImportDocs | null = null
  for (const record of Object.values(docs)) {
    if (record.status === "running" || record.status === "failed") continue
    const lastStem = record.items[record.items.length - 1]?.draft.stem ?? ""
    if (
      !usesSeededMidtermRead(record.name) &&
      !/vital sign change/i.test(lastStem)
    ) {
      continue
    }
    const seeded = mockExtractedQuestions(record.id)
    const cleared = withoutStaleReviewFlags(record.items, seeded)
    const current = cleared
      ? { ...record, items: cleared }
      : record
    if (cleared) {
      next ??= { ...docs }
      next[record.id] = current
    }
    if (extractionSignature(current.items) === extractionSignature(seeded)) continue
    if (
      importReviewHasProgress(current) ||
      importStemsDiverged(current.items, seeded)
    ) {
      continue
    }
    next ??= { ...docs }
    next[current.id] = {
      ...current,
      items: seeded,
      found: seeded.length,
    }
  }
  return next ?? docs
}

function settle(docs: QuestionImportDocs): QuestionImportDocs {
  let next: QuestionImportDocs | null = null
  const now = Date.now()
  for (const record of Object.values(docs)) {
    if (record.status !== "running" || now < record.finishAt) continue
    next ??= { ...docs }
    next[record.id] = {
      ...record,
      status: record.failure ? "failed" : "done",
    }
  }
  return next ?? docs
}

function syncTicker(docs: QuestionImportDocs) {
  if (typeof window === "undefined") return
  let deadline: number | null = null
  for (const record of Object.values(docs)) {
    if (record.status !== "running") continue
    if (deadline === null || record.finishAt < deadline) deadline = record.finishAt
  }
  // `read()` runs on every render, so scheduling has to be idempotent.
  if (deadline === tickerDeadline) return
  if (ticker !== null) {
    window.clearTimeout(ticker)
    ticker = null
  }
  tickerDeadline = deadline
  if (deadline === null) return

  ticker = window.setTimeout(
    () => {
      ticker = null
      tickerDeadline = null
      const previous = snapshot
      const settled = settle(previous)
      if (settled !== previous) {
        snapshot = settled
        persist(settled)
        announceImportTransitions(previous, settled)
      }
      emit()
    },
    Math.max(0, deadline - Date.now()) + 50
  )
}

function read(): QuestionImportDocs {
  if (!hydrated) {
    hydrated = true
    const raw = getStorageItem(STORAGE_KEY)
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw)
        const docs =
          typeof parsed === "object" &&
          parsed !== null &&
          (parsed as { v?: number }).v === VERSION
            ? (parsed as { d?: unknown }).d
            : null
        if (typeof docs === "object" && docs !== null) {
          const kept: QuestionImportDocs = {}
          for (const [id, value] of Object.entries(docs)) {
            if (isRecord(value)) kept[id] = value
          }
          snapshot = kept
        }
      } catch {
        // Unparseable payload. Nothing has been imported.
      }
    }
  }

  const previous = snapshot
  const settled = refreshSeededMidtermReads(settle(previous))
  if (settled !== previous) {
    snapshot = settled
    persist(settled)
    announceImportTransitions(previous, settled)
  }
  syncTicker(snapshot)
  return snapshot
}

function write(docs: QuestionImportDocs) {
  const previous = snapshot
  snapshot = docs
  hydrated = true
  persist(docs)
  syncTicker(docs)
  emit()
  announceImportTransitions(previous, docs)
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

export function startQuestionImport(input: {
  id: string
  name: string
  /**
   * What the read will turn up. Optional when `items` is given, since the
   * count is just their length and two sources of one number drift.
   */
  found?: number
  items?: ExtractedQuestion[]
  kind?: ImportSourceKind
  destinationFolderId?: string | null
  /** Required for a question-bank import. The course these questions are for. */
  courseId?: string | null
  asDraft?: boolean
  approach?: ImportApproach
  /** Question bank vs assessment. Defaults to assessment for older callers. */
  home?: QuestionImportHome
  /** Set to fail the run, for the demo's error state. */
  failure?: string
  /**
   * How long the read takes. A parsed spreadsheet has already been read by the
   * time this is called, so that path passes a short one rather than making
   * the author watch a fake clock over work that is finished.
   */
  runMs?: number
}): QuestionImportRecord {
  const now = Date.now()
  const items = input.items ?? []
  const record: QuestionImportRecord = {
    id: input.id,
    name: input.name,
    startedAt: now,
    finishAt: now + (input.runMs ?? QUESTION_IMPORT_MOCK_RUN_MS),
    status: "running",
    found: input.items ? items.length : (input.found ?? 0),
    failure: input.failure,
    kind: input.kind ?? "document",
    items,
    destinationFolderId: input.destinationFolderId ?? null,
    courseId: input.courseId ?? null,
    asDraft: input.asDraft ?? true,
    approach: input.approach ?? "review",
    home: input.home ?? "assessment",
  }
  write({ ...read(), [record.id]: record })
  return record
}

/** Applies a change to one record, if it is still there. */
function patchRecord(
  id: string,
  patch: (record: QuestionImportRecord) => QuestionImportRecord
) {
  const docs = read()
  const record = docs[id]
  if (!record) return
  write({ ...docs, [id]: patch(record) })
}

/**
 * Applies a change to some of a record's questions, leaving the rest alone.
 *
 * Questions already taken into the bank are never patched. Editing or
 * re-deciding one would change a review screen without changing the row the
 * author can see in the bank, which is worse than not offering it at all.
 */
function patchItems(
  jobId: string,
  matches: (item: ExtractedQuestion) => boolean,
  patch: (item: ExtractedQuestion) => ExtractedQuestion
) {
  patchRecord(jobId, (record) => ({
    ...record,
    items: record.items.map((item) =>
      item.importedAt === null && matches(item) ? patch(item) : item
    ),
  }))
}

export function setQuestionImportDecision(
  jobId: string,
  itemId: string,
  decision: ImportItemDecision
) {
  patchItems(
    jobId,
    (item) => item.id === itemId,
    (item) => ({ ...item, decision })
  )
}

/** One decision across a selection, for the bulk bar over the triage list. */
export function setQuestionImportDecisions(
  jobId: string,
  itemIds: readonly string[],
  decision: ImportItemDecision
) {
  const ids = new Set(itemIds)
  patchItems(
    jobId,
    (item) => ids.has(item.id),
    (item) => ({ ...item, decision })
  )
}

export function updateQuestionImportItem(
  jobId: string,
  itemId: string,
  patch: Partial<ImportedQuestionDraft>,
  extras?: { reviewResolved?: boolean }
) {
  patchItems(
    jobId,
    (item) => item.id === itemId,
    (item) => {
      const next: ExtractedQuestion = {
        ...item,
        draft: { ...item.draft, ...patch },
        ...(extras?.reviewResolved ? { reviewResolved: true } : {}),
      }
      // Applying the suggested fix has to lift the Import disable on the next
      // render. The draft is the source of truth; a stale attention flag
      // would leave the action grey after the field is already clean.
      if (isBlocked(item) && !isBlocked(next)) {
        next.reviewResolved = true
      }
      return next
    }
  )
}

/**
 * Settles a duplicate.
 *
 * `keep-new` and `keep-both` both clear the block, and the difference is what
 * happens to the existing question on commit, not to this one. Discarding is
 * not a resolution here because it is already spelled "reject", and giving the
 * same outcome two names would put two controls on screen for one decision.
 */
export function resolveQuestionImportDuplicate(
  jobId: string,
  itemId: string,
  resolution: ImportDuplicateResolution
) {
  patchItems(
    jobId,
    (item) => item.id === itemId,
    (item) => ({
      ...item,
      duplicateResolution: resolution,
      decision:
        resolution === "unresolved" ? item.decision : ("accepted" as const),
    })
  )
}

/** Batch settings: where the questions land and whether they land as drafts. */
export function setQuestionImportOptions(
  jobId: string,
  options: { destinationFolderId?: string | null; asDraft?: boolean }
) {
  patchRecord(jobId, (record) => ({
    ...record,
    destinationFolderId:
      options.destinationFolderId !== undefined
        ? options.destinationFolderId
        : record.destinationFolderId,
    asDraft: options.asDraft ?? record.asDraft,
  }))
}

/**
 * Takes specific questions into the destination now (Add to Pool / Import).
 *
 * Stamps `importedAt` on each id that is allowed to go in. Decisions are set to
 * accepted when taken so the record stays consistent with bank commit history.
 *
 * Pass `{ force: true }` for Import as draft: every selected item lands even
 * when triage would have blocked it (missing key, unresolved duplicate). Drafts
 * are meant to be finished in the pool, not held on the review screen.
 */
export function takeQuestionImportItems(
  jobId: string,
  itemIds: readonly string[],
  folderName: string | null,
  options?: { force?: boolean; asDraft?: boolean }
): { summary: QuestionImportCommit; taken: ExtractedQuestion[] } | null {
  const docs = read()
  const record = docs[jobId]
  if (!record || itemIds.length === 0) return null

  const idSet = new Set(itemIds)
  const at = Date.now()
  const taken: ExtractedQuestion[] = []
  const force = options?.force === true
  const asDraft = options?.asDraft ?? record.asDraft

  const items = record.items.map((item) => {
    if (!idSet.has(item.id) || item.importedAt !== null) return item
    let ready: ExtractedQuestion = { ...item, decision: "accepted" }
    // An unresolved duplicate used to stop Import and leave the row on
    // Duplicate. Keep both unless this is a forced draft take, which prefers
    // the new item. Blocked items still wait unless force is set.
    if (ready.duplicate && ready.duplicateResolution === "unresolved") {
      if (force || !isBlocked(ready)) {
        ready = {
          ...ready,
          duplicateResolution: force ? "keep-new" : "keep-both",
        }
      }
    }
    if (!force && !willImport(ready)) return item
    const stamped = { ...ready, importedAt: at }
    taken.push(stamped)
    return stamped
  })

  if (taken.length === 0) return null

  let rejected = 0
  let skipped = 0
  for (const item of items) {
    if (item.importedAt !== null) continue
    if (item.decision === "rejected") rejected += 1
    else skipped += 1
  }

  const summary: QuestionImportCommit = {
    at,
    imported: taken.length,
    rejected,
    skipped,
    folderName,
    asDraft,
  }

  write({
    ...docs,
    [jobId]: {
      ...record,
      asDraft,
      items,
      status: skipped > 0 ? "done" : "imported",
      commit: summary,
    },
  })
  return { summary, taken }
}

/**
 * Import as draft: commit every found question into the destination as drafts.
 *
 * Triage holds blocked / duplicate / low-confidence items for a human pass.
 * Skipping that pass means the whole batch lands in Imported — incomplete
 * drafts included — so the author finishes them there.
 */
export function commitDirectImportAsDrafts(
  jobId: string
): { summary: QuestionImportCommit; taken: ExtractedQuestion[] } | null {
  const docs = read()
  const record = docs[jobId]
  if (!record) return null
  if (record.status === "running" || record.status === "failed") return null

  const ids = record.items
    .filter(
      (item) => item.importedAt === null && item.decision !== "rejected"
    )
    .map((item) => item.id)
  if (ids.length === 0) return null

  return takeQuestionImportItems(jobId, ids, null, { force: true })
}

/**
 * Pulls questions back out of the destination (Remove from Pool / bank).
 *
 * Clears `importedAt` so they reappear as reviewable. Decisions stay accepted
 * until the author changes them — the author is undoing the take, not the read.
 */
export function untakeQuestionImportItems(
  jobId: string,
  itemIds: readonly string[]
): ExtractedQuestion[] {
  const docs = read()
  const record = docs[jobId]
  if (!record || itemIds.length === 0) return []

  const idSet = new Set(itemIds)
  const restored: ExtractedQuestion[] = []

  const items = record.items.map((item) => {
    if (!idSet.has(item.id) || item.importedAt === null) return item
    const next = { ...item, importedAt: null }
    restored.push(next)
    return next
  })

  if (restored.length === 0) return []

  write({
    ...docs,
    [jobId]: {
      ...record,
      items,
      status: "done",
      // Keep last commit summary for undo-of-commit; partial untakes leave it.
    },
  })
  return restored
}

/**
 * Takes every question that can go in (unblocked, duplicate settled) now.
 *
 * Partial by design. Anything still blocked or unresolved is counted as
 * skipped and left on the record, so the author can come back to the rest
 * without re-reading the file.
 */
export function commitQuestionImport(
  jobId: string,
  folderName: string | null
): QuestionImportCommit | null {
  const docs = read()
  const record = docs[jobId]
  if (!record) return null

  const readyIds = record.items
    .filter((item) => {
      if (item.importedAt !== null) return false
      if (item.decision === "rejected") return false
      if (isBlocked(item)) return false
      return true
    })
    .map((item) => item.id)
  if (readyIds.length === 0) return null
  return (
    takeQuestionImportItems(jobId, readyIds, folderName, { asDraft: false })
      ?.summary ?? null
  )
}

/**
 * Puts a committed batch back into review.
 *
 * The undo behind the banner on the bank. Decisions are untouched, so the
 * author lands back on exactly the screen they left rather than on a fresh
 * read of the same file.
 */
export function undoQuestionImportCommit(jobId: string) {
  patchRecord(jobId, (record) => {
    const { commit, ...rest } = record
    // Only the last commit is undone. On a batch committed in two passes the
    // earlier questions stay in the bank, which is what the author sees.
    const items = commit
      ? record.items.map((item) =>
          item.importedAt === commit.at ? { ...item, importedAt: null } : item
        )
      : record.items
    return { ...rest, items, status: "done" }
  })
}

/**
 * Forgets a document's record, putting the row back to unread. Covers cancel,
 * discard, and retry, which differ in what the author meant but not in what
 * the store has to hold afterwards.
 */
export function forgetQuestionImport(id: string) {
  const docs = read()
  if (!(id in docs)) return
  const next = { ...docs }
  delete next[id]
  write(next)
}

/** Marks a reviewed read as taken into the pool. */
export function takeQuestionImport(id: string) {
  const docs = read()
  const record = docs[id]
  if (!record) return
  write({ ...docs, [id]: { ...record, status: "imported" } })
}

/** Drops every record. Used by the demo reset and by tests. */
export function clearQuestionImports() {
  write(EMPTY)
}

/**
 * Re-read persisted import jobs and notify subscribers. Use from the import
 * sheet toolbar when the author wants a fresh pass over running / settled
 * jobs without closing the panel.
 */
export function refreshQuestionImportDocs(): void {
  hydrated = false
  read()
  emit()
}

export function useQuestionImportDocs(): QuestionImportDocs {
  return React.useSyncExternalStore(subscribe, read, () => EMPTY)
}

/** The newest read in flight, if any. Several may run; the banner picks this. */
export function questionImportMatchesHome(
  record: QuestionImportRecord,
  home: QuestionImportHome,
): boolean {
  if (home === "bank") return record.home === "bank"
  return record.home !== "bank"
}

/** Running read, else the newest settled outcome, for one surface only. */
export function latestQuestionImportBanner(
  docs: QuestionImportDocs,
  home: QuestionImportHome,
): QuestionImportRecord | null {
  const owned = Object.values(docs).filter((record) =>
    questionImportMatchesHome(record, home),
  )
  const running =
    owned
      .filter((record) => record.status === "running")
      .sort((a, b) => b.startedAt - a.startedAt)[0] ?? null
  if (running) return running
  return (
    owned
      .filter(
        (record) =>
          record.status === "done" ||
          record.status === "failed" ||
          record.status === "imported",
      )
      .sort((left, right) => {
        const rightAt =
          right.commit?.at ?? right.finishAt ?? right.startedAt ?? 0
        const leftAt = left.commit?.at ?? left.finishAt ?? left.startedAt ?? 0
        return rightAt - leftAt
      })[0] ?? null
  )
}

export function runningQuestionImport(
  docs: QuestionImportDocs,
  home?: QuestionImportHome,
): QuestionImportRecord | null {
  return (
    Object.values(docs)
      .filter(
        (record) =>
          record.status === "running" &&
          (home == null || questionImportMatchesHome(record, home)),
      )
      .sort((a, b) => b.startedAt - a.startedAt)[0] ?? null
  )
}

/** Reads that finished and turned something up, newest first. */
export function reviewableQuestionImports(
  docs: QuestionImportDocs
): QuestionImportRecord[] {
  return Object.values(docs)
    .filter((record) => record.status === "done" && record.items.length > 0)
    .sort((a, b) => b.finishAt - a.finishAt)
}

/**
 * The batch a bare "review my import" link should land on.
 *
 * Used by the old `/question-bank/import-review` path, which had no job in it
 * because there was only ever one hardcoded batch. Old links resolve to the
 * newest thing actually waiting rather than to a dead end.
 */
export function latestReviewableQuestionImport(
  docs: QuestionImportDocs
): QuestionImportRecord | null {
  return reviewableQuestionImports(docs)[0] ?? null
}

/** One record by id, for the review route. */
export function useQuestionImportRecord(
  jobId: string | undefined
): QuestionImportRecord | null {
  const docs = useQuestionImportDocs()
  return jobId ? (docs[jobId] ?? null) : null
}

/**
 * How long a running read has been going, on a one-second beat.
 *
 * **Elapsed, not remaining, and no percentage.** The read is a background job
 * that reports one thing: whether it is finished. It cannot say which page it
 * is on or how much is left, so a bar and a countdown would be numbers the
 * system invented. That is worse than saying nothing: a bar sitting at 60%
 * for a minute reads as a hang, and "30s left" that passes without finishing
 * teaches the author to distrust every estimate the product makes. Time spent
 * is the one honest number available, and it answers what the author actually
 * asks at minute three, which is whether anything is still happening.
 *
 * The store deliberately does not tick, so a surface that shows the clock has
 * to ask for the beat. Only those surfaces pay for it, and they stop paying
 * the moment the read settles.
 */
export function useQuestionImportElapsed(
  record: QuestionImportRecord | null
): string | null {
  const running = record?.status === "running"
  const [, tick] = React.useReducer((count: number) => count + 1, 0)

  React.useEffect(() => {
    if (!running) return
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [running])

  return record && running ? questionImportElapsed(record) : null
}

/** `1m 20s` since the read started, for the row, the banner, and the sheet. */
export function questionImportElapsed(record: QuestionImportRecord): string {
  const seconds = Math.max(0, Math.floor((Date.now() - record.startedAt) / 1000))
  const minutes = Math.floor(seconds / 60)
  return minutes > 0
    ? `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`
    : `${seconds}s`
}
